/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { classes, isNumber } from '@adobe-commerce/elsie/lib';
import { useCallback, useMemo } from 'preact/hooks';
import { ChevronDown } from '@adobe-commerce/elsie/icons';
import { Icon } from '@adobe-commerce/elsie/components/Icon';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@adobe-commerce/elsie/components/Pagination/Pagination.css';
import { PaginationButton } from './PaginationButton';

export interface PaginationProps {
  className?: string;
  currentPage?: number;
  totalPages?: number;
  onChange?: (currentPage: number, e?: Event) => void;
  routePage?: (page: number) => string;
}

export type PaginationList = {
  page: number | string;
  isActive: boolean;
  label: number | string;
};

export const Pagination: FunctionComponent<PaginationProps> = ({
  totalPages = 10,
  currentPage = 1,
  onChange,
  routePage,
  className,
  ...props
}) => {
  const translations = useText({
    backwardButton: 'Dropin.Pagination.backwardButton.ariaLabel',
    forwardButton: 'Dropin.Pagination.forwardButton.ariaLabel',
  });

  const handleForward = useCallback((e?: Event) => {
    const nextPage = Math.min(currentPage + 1, totalPages);

    onChange?.(nextPage, e);
  }, [currentPage, onChange, totalPages]);

  const handleBackward = useCallback((e?: Event) => {
    const prevPage = Math.max(currentPage - 1, 1);

    onChange?.(prevPage, e);
  }, [currentPage, onChange]);

  const handleSetPage = useCallback(
    (currentPage: number | string, e?: Event) => {
      if (isNumber(currentPage)) {
        onChange?.(currentPage as number, e);
      }
    },
    [onChange]
  );

  const createPages = useCallback(
    (currentPage: number, totalPages: number): PaginationList[] => {
      let pages: PaginationList[] = [];

      const addPageRange = (start: number, end: number) => {
        for (let i = start; i <= end; i++) {
          pages.push({ page: i, isActive: i === currentPage, label: i });
        }
      };

      if (totalPages <= 5) {
        addPageRange(1, totalPages);
      } else if (currentPage <= 2) {
        addPageRange(1, 2);
        pages.push({ page: 'ellipsis', isActive: false, label: '...' });
        addPageRange(totalPages - 1, totalPages);
      } else if (currentPage >= totalPages - 3) {
        addPageRange(totalPages - 4, totalPages);
      } else {
        addPageRange(currentPage - 1, currentPage);
        pages.push({ page: 'ellipsis', isActive: false, label: '...' });
        addPageRange(totalPages - 1, totalPages);
      }

      return pages;
    },
    []
  );

  const paginationList: PaginationList[] = useMemo(
    () => createPages(currentPage, totalPages),
    [createPages, currentPage, totalPages]
  );

  return (
    <div {...props} className={classes(['dropin-pagination', className])}>
      <PaginationButton
        data-testid="prev-button"
        aria-label={translations.backwardButton}
        disabled={currentPage === 1}
        onClick={(e: Event) => handleBackward(e)}
        href={routePage?.(currentPage) ?? undefined}
        className={classes([
          'dropin-pagination-arrow',
          'dropin-pagination-arrow--backward',
          ['dropin-pagination-arrow--disabled', currentPage === 1],
        ])}
      >
        <Icon size="24" source={ChevronDown} />
      </PaginationButton>
      <ul className="dropin-pagination_list">
        {(paginationList as PaginationList[]).map((item, index) => (
          <li
            data-testid={`dropin-pagination_list-item--${item.page}`}
            key={`${item.page}_${index}`}
            className={classes([
              'dropin-pagination_list-item',
              `dropin-pagination_list-item--${item.page}`,
              [`dropin-pagination_list-item--active`, item.isActive],
            ])}
          >
            <PaginationButton
              data-testid={`set-page-button-${item.page}`}
              onClick={(e: Event) => handleSetPage(item.page, e)}
              href={routePage?.(item.page as number) ?? undefined}

            >
              {item.label}
            </PaginationButton>
          </li>
        ))}
      </ul>
      <PaginationButton
        data-testid="next-button"
        aria-label={translations.forwardButton}
        disabled={currentPage === totalPages}
        onClick={(e: Event) => handleForward(e)}
        href={routePage?.(currentPage) ?? undefined}
        className={classes([
          'dropin-pagination-arrow',
          'dropin-pagination-arrow--forward',
          ['dropin-pagination-arrow--disabled', currentPage === totalPages],
        ])}
      >
        <Icon size="24" source={ChevronDown} />
      </PaginationButton>
    </div>
  );
};
